import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  TextField,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading } from "../../Context/Contexts";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { ListQuery, SupplierListViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { useTranslation } from "react-i18next";
import { SupplierAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import ResponsivePagination from "../../components/ResponsivePagination";
import BlockIcon from "@mui/icons-material/Block";



const SupplierList: React.FC = () => {
    const [suppliers, setSuppliers] = useState<SupplierListViewModel[]>([]);
    const { t } = useTranslation();

    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();

    const fetchSuppliers = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: SupplierListViewModel[]; count: number }>(
                "/supplierApi/GetSuppliers",
                query
            );
            setSuppliers(response.items);
            setTotalCount(response.count);
        } catch (error) {
            console.error("Failed to fetch customers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);



    const isBooleanProperty = filterProperty === "ext_blocked";

    useEffect(() => {
        if (isBooleanProperty) setFilter("true");
    }, [isBooleanProperty]);


    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }
        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(
                (f) => f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;
            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            setQuery({ ...query, filters: query.filters, page: 0, filter });
        }
        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelectCustomer = (supplierNo: string) => {
        navigate({ path: "/purchase/supplier/edit", params: { supplierNo } });
    };

    const handleCreateSupplier = () => {
        navigate({ path: "/purchase/supplier/edit", params: { supplierNo: "" } });
    };

    return (
        <CenteredListCard title={ t("common.suppliers")}>

            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreateSupplier}
                >
              {t("common.create")}
                </Button>
            </Box>

            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_No">{t("common.number")}</option>
                            <option value="ext_Name">{t("common.name")}</option>
                            <option value="ext_EMail">{t("common.email")}</option>
                            <option value="ext_blocked">{t("common.blocked")}</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>

                        {(() => {
                            switch (filterProperty) {
                                case "ext_No":
                                    return (
                                        <SupplierAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                    );
                                case "ext_blocked":
                                    return (
                                        <TextField
                                            select
                                            label={t("common.value")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(e) => setFilter(e.target.value)}
                                            value={filter}
                                            SelectProps={{ native: true }}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <option value="true">{t("common.yes")}</option>
                                            <option value="false">{t("common.no")}</option>
                                        </TextField>
                                    );
                                default:
                                    return (
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(event) => setFilter(event.target.value)}
                                            value={filter}
                                            disabled={loading}
                                        />
                                    );
                            }
                        })()}
         
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>


            <Grid container spacing={2} sx={{ mb: 2 }}>
                {suppliers.map((supplier) => (
                    <Grid item
                        xs={12}
                        sm={suppliers.length === 1 ? 12 : suppliers.length === 2 ? 6 : 6}
                        md={suppliers.length === 1 ? 12 : suppliers.length === 2 ? 6 : 4}
                        key={supplier.no}>
                        <Card
                            onClick={() => handleSelectCustomer(supplier.no)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<LocalShippingIcon fontSize="small" color="action" />}
                                    title={supplier.name}
                                    status={supplier.blocked
                                        ? {
                                            label: t("common.blocked"),
                                            color: "warning",
                                            icon: <BlockIcon fontSize="small" />,
                                        }
                                        : undefined}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.number")}: {supplier.no}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.address")}: {supplier.address}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.email")}: {supplier.eMail}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {!loading && suppliers.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalCount}

                onPageChange={handlePageChange}

            />
         
        </CenteredListCard>
    );
};

export default SupplierList;